import { Injectable, signal, computed, NgZone, inject } from '@angular/core';

/**
 * Tracks browser connectivity for high-latency / intermittent networks.
 *
 * `online()` is a signal derived from `navigator.onLine` plus the
 * window-level `online`/`offline` events. Components can subscribe via
 * the signal or read it imperatively.
 *
 * The events fire outside Angular's zone in some browsers, so we
 * `run()` the update inside NgZone to keep change detection honest.
 */
@Injectable({ providedIn: 'root' })
export class NetworkStatusService {
  private zone = inject(NgZone);
  private readonly _online = signal<boolean>(this.readNavigatorOnLine());

  readonly online = computed(() => this._online());
  readonly offline = computed(() => !this._online());

  constructor() {
    if (typeof window === 'undefined') return;
    window.addEventListener('online', () => this.update());
    window.addEventListener('offline', () => this.update());
  }

  private update() {
    const next = this.readNavigatorOnLine();
    this.zone.run(() => this._online.set(next));
  }

  private readNavigatorOnLine(): boolean {
    return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
  }
}
